import { NGXLogger } from 'ngx-logger';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpRequest, HttpEvent, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, from, of, firstValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { AppConstants } from 'src/app/commons/app-constants';
import { HttpParams } from '@angular/common/http';
import { Page } from 'src/app/commons/models/page';
import { LoadingSite } from '../model/loading-site';
import { DetailCacheService } from 'src/app/offline/detail-cache.service';
import { NetworkStatusService } from 'src/app/services/network-status.service';
import { OutboxService, generateProvisionalId } from 'src/app/offline/outbox.service';
import { offlineDb, scopedKey } from 'src/app/offline/offline-db';
import { isOfflineCapableNative } from 'src/app/commons/utils/platform';
import { runDeltaListSync, evictStaleRowFromCache } from 'src/app/offline/delta-merge.util';
import { PrefetchGateService } from 'src/app/offline/prefetch-gate.service';
import { FULL_LIST_MAX, getFullList, saveFullList } from 'src/app/offline/offline-window';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

// Skip caching anything bigger than this offline — matches ProductService/BusinessPartnerService's ceiling.
const MAX_CACHE_FILE_SIZE = 10 * 1024 * 1024;

@Injectable({
  providedIn: 'root'
})
export class LoadingSiteService {
  private readonly logger = inject(NGXLogger);
  private readonly prefetchGate = inject(PrefetchGateService);


  constructor(
    private http: HttpClient,
    private detailCacheService: DetailCacheService,
    private networkStatusService: NetworkStatusService,
    private outboxService: OutboxService,
  ) { }

  getData(): Observable<LoadingSite> {
    return this.http.get<LoadingSite>(AppConstants.API_URL + 'api/v1/loading-sites');
    //return this.http.get<any>(AppConstants.API_URL + 'all', { responseType: 'text' });
  }

  postData(data: LoadingSite): Observable<LoadingSite> {
    if (isOfflineCapableNative() && !this.networkStatusService.isOnline) {
      return from(this.createOffline(data));
    }
    return this.http.post<LoadingSite>(AppConstants.API_URL + 'api/v1/loading-site', data, { responseType: 'json' });
  }

  // Fabricates a response shaped like a real LoadingSite so callers (the form's submit
  // handler, which discards the response body entirely) work unmodified. The actual create is
  // queued for SyncEngineService to replay once back online. Also merges the fabricated site
  // directly into the offline masterCache 'loadingSites' list so it shows up immediately in
  // the TU form's autocomplete — the actual point of offline loading-site creation.
  private async createOffline(data: LoadingSite): Promise<LoadingSite> {
    const clientUuid = uuidv4();
    const provisionalId = generateProvisionalId();
    const provisionalEncodedId = `offline-${clientUuid}`;

    const fakeResponse: any = {
      ...data,
      loadingSiteId: provisionalId,
      encodedId: provisionalEncodedId,
      // Not part of the real DTO — internal marker so UI code can show an "unsynced" badge.
      _offlineClientUuid: clientUuid,
    };

    await offlineDb.detailCache.put({ key: scopedKey(`loadingSite:${provisionalEncodedId}`), data: fakeResponse, fetchedAt: Date.now() });

    const entry = await offlineDb.masterCache.get(scopedKey('loadingSites'));
    const list = Array.isArray(entry?.data) ? (entry!.data as any[]) : [];
    await offlineDb.masterCache.put({ key: scopedKey('loadingSites'), data: [...list, fakeResponse], fetchedAt: entry?.fetchedAt ?? Date.now() });

    const outboxPayload = { ...data, clientUuid };
    await this.outboxService.enqueue('CREATE_LOADING_SITE', clientUuid, outboxPayload);
    return fakeResponse;
  }

  getByLoadingSiteName(name: string): Observable<any | undefined> {
    const response = this.http.get(AppConstants.API_URL + `api/v1/check-loading-site-name?name=${name}`, httpOptions);
    this.logger.debug(`getByLoadingSiteName Response is ${JSON.stringify(response)}`)
    return response;
  }


  getListLoadingSites(httpParams?: HttpParams): Observable<Page<LoadingSite>> {
    let url = `${AppConstants.API_URL}api/v1/list-loading-sites?`;
    if (httpParams) {
      url = url + httpParams;
    }
    this.logger.debug(`url is ${url}`)
    return this.http.get<Page<LoadingSite>>(url);
  }

  // Delta-sync fetch (Increment 6): on first-ever sync (modifiedSince=null) requests exactly the
  // same size=10&page=0 as before, matching LoadingSiteListComponent's default query, so the
  // merged cache stays a drop-in replacement for what that component reads offline. On a delta
  // sync, size is widened to comfortably capture every row touched since the last sync in one
  // page.
  private fetchLoadingSitesPage(modifiedSince: string | null): Observable<HttpResponse<Page<LoadingSite>>> {
    let params = new HttpParams().set('size', modifiedSince ? 200 : 10).set('page', 0);
    if (modifiedSince) params = params.set('modifiedSince', modifiedSince);
    return this.http.get<Page<LoadingSite>>(`${AppConstants.API_URL}api/v1/list-loading-sites?${params.toString()}`, { observe: 'response' });
  }

  // Called by OfflinePrefetchService (Android only) so the list's first page is already cached
  // before the user ever opens Loading Sites. Delegates to runDeltaListSync (Increment 6) —
  // first sync ever caches the full response as-is; every sync after that merges only what's
  // changed into the existing cached top-10 window instead of replacing it wholesale.
  // Returns a Promise (resolving once the LIST itself is fetched/cached — never rejects, this
  // is always best-effort) so OfflinePrefetchService's per-area progress checklist can show a
  // meaningful "done" instead of resolving instantly. Per-row detail/document prefetching
  // below stays fire-and-forget/background, same as ProductService's equivalent.
  // Phase 2 / D7 (OFFLINE_PLAN.md, 2026-09-29): the whole list (up to FULL_LIST_MAX rows, one request), so
  // this list screen can search / sort / page offline — before, offline showed only its first 10 rows.
  // Best-effort: a failure keeps the last saved list.
  private async saveFullListForOffline(): Promise<void> {
    if (!isOfflineCapableNative()) return;
    try {
      const res = await firstValueFrom(this.http.get<Page<LoadingSite>>(`${AppConstants.API_URL}api/v1/list-loading-sites?size=${FULL_LIST_MAX}&page=0`));
      if (Array.isArray(res?.content)) await saveFullList('loadingSites', res.content);
    } catch { /* keep the last saved list */ }
  }

  async prefetchDefaultList(): Promise<void> {
    await this.saveFullListForOffline();
    const cacheKey = `loadingSiteList:${new HttpParams().set('size', 10).set('page', 0).toString()}`;
    const result = await runDeltaListSync<LoadingSite>({
      entity: 'loadingSites',
      cacheKey,
      fetchPage: (modifiedSince) => this.fetchLoadingSitesPage(modifiedSince),
      deletionsEntityType: 'LOADING_SITE',
      mergeConfig: {
        getId: (l) => l.loadingSiteId,
        getCreatedDate: (l) => l.createdDate ?? '',
        windowSize: 10,
      },
      http: this.http,
    });
    // A deleted loading site's cached detail record is otherwise stranded in IndexedDB forever
    // once evicted from the list — see delta-merge.util.ts's evictedRows doc comment. Document
    // blobs (siteDocument:${id}, keyed by document id) can't be derived without a fetch this
    // site can no longer serve, so those are left as a small residual orphan (bounded by the
    // existing 10MB per-file cap) — only the detail record itself is reclaimed here.
    result?.evictedRows?.forEach(row => {
      const id = row.encodedId || row.loadingSiteId;
      if (isOfflineCapableNative() && id) offlineDb.detailCache.delete(scopedKey(`loadingSite:${id}`));
    });
    // Every leaf call routed through PrefetchGateService — bounded concurrency, out of Angular's
    // zone, yielding between units. See PrefetchGateService for the unthrottled-fan-out freeze
    // this fixes. Still fire-and-forget.
    for (const row of (result?.page.content ?? [])) {
      // Same key navigation/the resolver actually look up by — see
      // LoadingSiteListComponent.prefetchDetailCache for the full explanation.
      const id = row.encodedId || row.loadingSiteId;
      // Raw http.get(), not getLoadingSiteById()/cacheThrough — see BusinessPartnerService's
      // identical prefetchDefaultList comment for why cacheThrough's stale-cache fallback
      // would otherwise mask the true 404 this self-heal needs to see.
      void this.prefetchGate.run(async () => {
        try {
          const data = await firstValueFrom(this.http.get<LoadingSite>(`${AppConstants.API_URL}api/v1/loading-sites/${id}`));
          if (isOfflineCapableNative()) {
            await offlineDb.detailCache.put({ key: scopedKey(`loadingSite:${id}`), data, fetchedAt: Date.now() });
          }
        } catch (err) {
          if ((err as HttpErrorResponse)?.status === 404) {
            evictStaleRowFromCache<LoadingSite>(cacheKey, row.loadingSiteId, (r) => r.loadingSiteId);
          }
        }
      });
      this.cacheDocumentsForRow(String(id));
    }
  }

  // Caches every compliance document's bytes (all file types, not images-only) so
  // SiteDocumentsComponent renders offline instead of showing broken previews/dead links. Goes
  // through the /content proxy (never doc.url/thumbnailUrl directly) for the same CORS reason
  // as ProductService.cachePhotoBlob — those fields are raw signed B2 URLs.
  private cacheDocumentsForRow(siteId: string): void {
    if (!siteId) return;
    void this.prefetchGate.run(async () => {
      const docs = await firstValueFrom(this.getSiteDocuments(siteId)).catch(() => null);
      if (docs) this.cacheDocumentBlobs(siteId, docs);
    });
  }

  // Called by SiteDocumentsComponent after its own (online) loadDocs() succeeds — a Loading
  // Site outside the last-10 prefetch window still gets its document blobs cached the moment
  // it's actually viewed, same "cache what's been seen" pattern as
  // ProductComponent.loadPhotos()/BusinessPartnerService.cacheAttachmentBlobs. Without this,
  // only the rare prefetch batch would ever cache blobs, leaving every other site's documents
  // permanently un-cacheable.
  cacheDocumentBlobs(siteId: string, docs: any[]): void {
    if (!isOfflineCapableNative()) return;
    docs.forEach(doc => {
      if (doc.size && doc.size > MAX_CACHE_FILE_SIZE) return;
      this.cacheDocumentBlob(siteId, doc.id);
    });
  }

  // Gated so opening a site with many documents doesn't fire every blob download at once.
  private cacheDocumentBlob(siteId: string, docId: number): void {
    void this.prefetchGate.run(async () => {
      try {
        const blob = await firstValueFrom(
          this.http.get(`${AppConstants.API_URL}api/v1/loading-sites/${siteId}/documents/${docId}/content`, { responseType: 'blob' }));
        await offlineDb.detailCache.put({ key: `siteDocument:${docId}`, data: blob, fetchedAt: Date.now() });
      } catch { /* best-effort */ }
    });
  }

  getListLoadingSitesGlobalFilter(httpParams?: HttpParams): Observable<Page<LoadingSite>> {
    let url = `${AppConstants.API_URL}api/v1/loading-sites/search?`;
    if (httpParams) {
      url = url + httpParams;
    }
    this.logger.debug(`url is ${url}`)
    return this.http.get<Page<LoadingSite>>(url);
  }  ///buyers/{id}

  /** Deactivate instead of delete: kept in history, hidden from new work. */
  deactivate(id: string): Observable<LoadingSite> {
    return this.http.post<LoadingSite>(`${AppConstants.API_URL}api/v1/loading-site/${id}/deactivate`, {});
  }

  reactivate(id: string): Observable<LoadingSite> {
    return this.http.post<LoadingSite>(`${AppConstants.API_URL}api/v1/loading-site/${id}/reactivate`, {});
  }

  deleteListLoadingSites(id: any, forceDelete: boolean = false): Observable<string> {
    let url = `${AppConstants.API_URL}api/v1/loading-sites/${id}?forceDelete=${forceDelete}`;
    this.logger.debug(`url is ${url}`)
    return this.http.delete<string>(url);
  }

   deleteLoadingSite(id: any, forceDelete: boolean = false): Observable<string> {
    let url = `${AppConstants.API_URL}api/v1/loading-site/${id}?forceDelete=${forceDelete}`;
    this.logger.debug(`url is ${url}`)
    return this.http.delete<string>(url);
  }

  

  getLoadingSiteById(id: any): Observable<LoadingSite> {
    const url = AppConstants.API_URL + 'api/v1/loading-sites/' + id;
    this.logger.debug(`[OPAQUE-ID][LoadingSite Service] getLoadingSiteById Url is ${url}`);
    // Offline with no saved copy: its row from the full list saved on the phone (2026-09-29).
    return this.detailCacheService.cacheThrough(`loadingSite:${id}`, () => this.http.get<LoadingSite>(url),
      async () => (await getFullList('loadingSites'))?.rows
        .find((r: any) => r?.encodedId === id || String(r?.loadingSiteId) === String(id)));
  }

  search(query: string): Observable<LoadingSite[]> {
    const url = `${AppConstants.API_URL}api/v1/loading-sites/search-query?query=${query}`;
    this.logger.debug(`Url is ${url}`)
    if (!query.trim()) {
      return of([]); // Return an empty observable if query is blank or whitespace
    }
    return this.http.get<LoadingSite[]>(url);
  }

  // ── Site Documents ────────────────────────────────────────────────────────

  // Wrapped in DetailCacheService.cacheThrough — without this, even though the document BLOBS
  // get cached (cacheDocumentBlob), there was no offline-available copy of the document LIST
  // metadata itself, so the component could never discover which blobs exist to render offline
  // (same bug shape as ProductService.getProductPhotos()/BusinessPartnerService.getAttachments()).
  getSiteDocuments(siteId: string): Observable<any[]> {
    return this.detailCacheService.cacheThrough<any[]>(`siteDocuments:${siteId}`, () =>
      this.http.get<any[]>(`${AppConstants.API_URL}api/v1/loading-sites/${siteId}/documents`)
    );
  }

  uploadSiteDocuments(siteId: string, files: File[], category: string): Observable<HttpEvent<any>> {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    formData.append('category', category);
    const req = new HttpRequest('POST',
      `${AppConstants.API_URL}api/v1/loading-sites/${siteId}/documents/upload`,
      formData, { reportProgress: true });
    return this.http.request(req);
  }

  deleteSiteDocument(siteId: string, docId: number): Observable<any> {
    return this.http.delete(`${AppConstants.API_URL}api/v1/loading-sites/${siteId}/documents/${docId}`);
  }

  updateSiteDocumentCategory(siteId: string, docId: number, category: string): Observable<any> {
    return this.http.patch(
      `${AppConstants.API_URL}api/v1/loading-sites/${siteId}/documents/${docId}/metadata?category=${encodeURIComponent(category)}`,
      {}
    );
  }
}
